import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">
            分类未找到
          </h1>
          <Link
            to="/"
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const bannerImageUrl = category.bannerImageUrl;

  const pageTitle = `${category.name} - 太子千秋万载 | 皇储祝颂文化`;
  const pageDesc = category.description;

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`太子千秋万载,${category.name},皇太子祝颂礼仪,古代储君文化`}
        canonicalUrl={`https://yanxiya.cn/category/${category.slug}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <section className="relative w-full h-64 md:h-80 flex items-center overflow-hidden">
            <img
              src={bannerImageUrl}
              alt={category.name}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
            <div className="relative z-10 w-full px-4 md:px-8">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center gap-2 text-background-300 text-sm mb-3">
                  <i className={`${category.icon}`}></i>
                  <span>{category.articleCount} 篇文章</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-3">
                  {category.name}
                </h1>
                <p className="text-sm md:text-base text-background-200 max-w-2xl">
                  {category.description}
                </p>
              </div>
            </div>
          </section>

          <Breadcrumb
            items={[
              { name: category.name, path: `/category/${category.slug}` },
            ]}
          />

          <section className="w-full px-4 md:px-8 py-10">
            <div className="max-w-7xl mx-auto">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.id} {...article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                  </div>
                  <h2 className="text-lg font-heading font-semibold text-foreground-700 mb-2">
                    暂无文章
                  </h2>
                  <p className="text-sm text-foreground-500">
                    该分类下暂无文章，请稍后再来
                  </p>
                </div>
              )}

              <div className="mt-12 flex items-center justify-center gap-4">
                <Link
                  to="/"
                  className="px-6 py-2.5 text-sm font-medium rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-1.5"></i>
                  返回首页
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}